import { Link, useLocation, Navigate } from "react-router-dom";
import { MapPin, ShieldCheck, Clock, BadgeCheck, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { CTAPair } from "../components/CTAButtons";
import { FAQAccordion } from "../components/FAQSection";
import { CTABand } from "../components/CTABand";
import { locationBySlug, LOCATIONS } from "../data/locations";
import { SERVICE_NAV } from "../data/services";

const TRUST = [
  { icon: ShieldCheck, text: "Warranty-backed parts & labor, in writing" },
  { icon: BadgeCheck, text: "Exact upfront quote — no surprise charges" },
  { icon: Clock, text: "Most jobs done in 60–90 minutes on site" },
];

export default function LocationPage() {
  const { pathname } = useLocation();
  const loc = locationBySlug(pathname.replace(/^\//, ""));
  if (!loc) return <Navigate to="/service-areas" replace />;

  const others = LOCATIONS.filter((l) => l.slug !== loc.slug).slice(0, 4);

  return (
    <>
      <SEO title={loc.metaTitle} description={loc.metaDesc} path={`/${loc.slug}`} />

      <section className="grain relative overflow-hidden border-b border-edge">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <Reveal>
            <Eyebrow className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> {loc.city}, {loc.state}
            </Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Mobile Mechanic in <span className="text-crimson">{loc.city}, {loc.st}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{loc.intro}</p>
            <CTAPair className="mt-8" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50">
              Brake Service That Comes to {loc.city}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{loc.local}</p>
            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber2">Areas we serve in {loc.city}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {loc.spots.map((s) => (
                  <span key={s} className="border border-edge bg-slate2 px-3 py-1.5 text-xs font-semibold text-slate-300">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-edge bg-slate2 p-7">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">
                Why {loc.city} Drivers Book Us
              </h2>
              <ul className="mt-5 space-y-4">
                {TRUST.map((t) => (
                  <li key={t.text} className="flex items-start gap-3">
                    <t.icon className="mt-0.5 h-5 w-5 shrink-0 text-amber2" />
                    <span className="text-sm leading-relaxed text-slate-300">{t.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-edge pt-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Trusted by nearly 2,000,000 followers across social media
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-edge bg-slate2">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50">
              {loc.city} Brake FAQs
            </h2>
          </Reveal>
          <Reveal className="mt-8">
            <FAQAccordion items={loc.faqs} testidPrefix={`${loc.city.toLowerCase()}-faq`} />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-50">
            Related Services & Nearby Towns
          </h2>
        </Reveal>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/brake-replacement"
            data-testid="location-brake-service-link"
            className="group flex items-center justify-between border border-crimson bg-crimson px-5 py-4 transition-colors hover:bg-crimsondark"
          >
            <span className="font-display text-lg font-bold uppercase text-white">
              Mobile Brake Replacement
            </span>
            <ArrowRight className="h-4 w-4 text-white" />
          </Link>
          {SERVICE_NAV.filter((n) => !n.featured).map((n) => (
            <Link
              key={n.slug}
              to={n.slug}
              data-testid={`location-service-${n.value}-link`}
              className="group flex items-center justify-between border border-edge bg-slate2 px-5 py-4 transition-colors hover:border-crimson"
            >
              <span className="font-display text-lg font-bold uppercase text-slate-100 group-hover:text-crimson">
                {n.label} in {loc.city}
              </span>
              <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-crimson" />
            </Link>
          ))}
          {others.map((l) => (
            <Link
              key={l.slug}
              to={`/${l.slug}`}
              data-testid={`location-nearby-${l.city.toLowerCase()}-link`}
              className="group flex items-center justify-between border border-edge bg-slate2 px-5 py-4 transition-colors hover:border-amber2/60"
            >
              <span className="font-display text-lg font-bold uppercase text-slate-200">
                Brakes in {l.city}, {l.st}
              </span>
              <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-amber2" />
            </Link>
          ))}
        </div>
      </section>

      <CTABand title={`${loc.city} Brakes, Fixed in Your Driveway`} />
    </>
  );
}
